import { cn } from '@/lib/utils';
import { ArrowUp, Square } from 'lucide-react';

import {
  useChatData,
  useChatInteract,
  useChatMessages
} from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Translator } from 'components/i18n';

interface SubmitButtonProps {
  disabled?: boolean;
  onSubmit: () => void;
}

export default function SubmitButton({
  disabled,
  onSubmit
}: SubmitButtonProps) {
  const { loading } = useChatData();
  const { firstInteraction } = useChatMessages();
  const { stopTask } = useChatInteract();

  return (
    <TooltipProvider>
      {loading && firstInteraction ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="stop-button"
              aria-label="Stop generating"
              onClick={stopTask}
              size="icon"
              className="size-9 rounded-[10px] bg-foreground text-background shadow-none transition-[background-color,transform] duration-150 hover:bg-foreground/90 active:scale-[0.96] focus-visible:ring-[3px] focus-visible:ring-foreground/15"
            >
              <Square className="!size-3.5 fill-current stroke-[2]" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              <Translator path="chat.input.actions.stop" />
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="chat-submit"
              aria-label="Send message"
              disabled={disabled}
              onClick={onSubmit}
              size="icon"
              className={cn(
                'size-9 rounded-[10px] transition-[background-color,color,box-shadow,transform] duration-150 ease-out focus-visible:ring-[3px] focus-visible:ring-primary/20 disabled:scale-100 disabled:opacity-100',
                disabled
                  ? 'bg-muted/75 text-muted-foreground/40 shadow-none'
                  : 'bg-primary text-white shadow-[0_3px_10px_-6px_hsl(var(--primary))] hover:bg-primary/90 hover:shadow-[0_4px_12px_-6px_hsl(var(--primary))] active:scale-[0.96] active:bg-primary/85'
              )}
            >
              <ArrowUp className="!size-[17px] stroke-[2.2]" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              <Translator path="chat.input.actions.send" />
            </p>
          </TooltipContent>
        </Tooltip>
      )}
    </TooltipProvider>
  );
}
